import type { BlockSerialized } from '#layers/block-engine/types/block';
import { createEventHook } from '@vueuse/core';

export interface BlockAdapterEventPayload<TResult> {
	handled: boolean;
	result: TResult;
	markHandled: () => void;
	setResult: (result: TResult) => void;
}

export interface LoadBlockSerializedPayload extends BlockAdapterEventPayload<BlockSerialized | undefined> {
	id: string;
}

export interface LoadBlocksSerializedPayload extends BlockAdapterEventPayload<Array<BlockSerialized>> {}

export interface SaveBlockSerializedPayload extends BlockAdapterEventPayload<undefined> {
	block: BlockSerialized;
}

export const blockAdapterEvents = {
	loadBlockSerialized: createEventHook<LoadBlockSerializedPayload>(),
	loadBlocksSerialized: createEventHook<LoadBlocksSerializedPayload>(),
	saveBlockSerialized: createEventHook<SaveBlockSerializedPayload>(),
};

async function loadBlockSerialized(id: string): Promise<BlockSerialized> {
	const payload = createEventPayload<BlockSerialized | undefined>();

	await blockAdapterEvents.loadBlockSerialized.trigger({
		id,
		...payload,
	});

	if (!payload.handled) {
		throw new Error('No block adapter handler registered for loadBlockSerialized');
	}

	if (!payload.result) {
		throw new Error(`Block with id ${id} not found`);
	}

	return payload.result;
}

async function loadBlocksSerialized(): Promise<Array<BlockSerialized>> {
	const payload = createEventPayload<Array<BlockSerialized>>([]);

	await blockAdapterEvents.loadBlocksSerialized.trigger(payload);

	if (!payload.handled) {
		throw new Error('No block adapter handler registered for loadBlocksSerialized');
	}

	return payload.result;
}

async function saveBlockSerialized(block: BlockSerialized): Promise<void> {
	const payload = createEventPayload<undefined>();

	await blockAdapterEvents.saveBlockSerialized.trigger({
		block,
		...payload,
	});

	if (!payload.handled) {
		throw new Error('No block adapter handler registered for saveBlockSerialized');
	}
}

export function useBlockAdapter() {
	return {
		loadBlockSerialized,
		loadBlocksSerialized,
		saveBlockSerialized,
	};
}

function createEventPayload<TResult = undefined>(initialResult?: TResult): BlockAdapterEventPayload<TResult> {
	const payload: BlockAdapterEventPayload<TResult> = {
		handled: false,
		result: initialResult as TResult,
		markHandled() {
			payload.handled = true;
		},
		setResult(result) {
			payload.handled = true;
			payload.result = result;
		},
	};

	return payload;
}
